import { useMemo, useState } from "react";

import type { Section } from "../types/template";

export const useTemplateSections = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSections, setSelectedSections] = useState<Section[]>([]);
  const [availableSections, setAvailableSections] = useState<Section[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [checkedSections, setCheckedSections] = useState<Set<string>>(new Set());
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Filter and search sections
  const unselectedSections = useMemo(() => {
    const filtered = searchQuery
      ? availableSections.filter((section) =>
          section.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : availableSections;

    return filtered.filter(
      (section) => !selectedSections.some((selected) => selected.id === section.id)
    );
  }, [availableSections, searchQuery, selectedSections]);

  const handleSelectAll = (checked: boolean) => {
    setSelectAllChecked(checked);
    setCheckedSections(
      checked ? new Set(unselectedSections.map((s) => s.id)) : new Set()
    );
  };

  const handleAddSelected = () => {
    const sectionsToAdd = unselectedSections.filter((section) =>
      checkedSections.has(section.id)
    );
    setSelectedSections((prev) => [...prev, ...sectionsToAdd]);
    setCheckedSections(new Set());
    setSelectAllChecked(false);
  };

  const handleCheckSection = (sectionId: string, checked: boolean) => {
    setCheckedSections((prev) => {
      const newChecked = new Set(prev);
      if (checked) {
        newChecked.add(sectionId);
      } else {
        newChecked.delete(sectionId);
        setSelectAllChecked(false);
      }
      return newChecked;
    });
  };

  const handleRemoveSection = (sectionId: string) => {
    setSelectedSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    setSelectedSections((prev) => {
      const newSections = [...prev];
      const [draggedSection] = newSections.splice(draggedIndex, 1);
      const insertIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      newSections.splice(insertIndex, 0, draggedSection);
      return newSections;
    });
    setDraggedIndex(null);
  };

  const reset = () => {
    setSearchQuery("");
    setSelectedSections([]);
    setAvailableSections([]);
    setSelectAllChecked(false);
    setCheckedSections(new Set());
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return {
    // State
    searchQuery,
    selectedSections,
    availableSections,
    selectAllChecked,
    checkedSections,
    draggedIndex,
    dragOverIndex,
    unselectedSections,

    // Setters
    setSearchQuery,
    setSelectedSections,
    setAvailableSections,

    // Handlers
    handleSelectAll,
    handleAddSelected,
    handleCheckSection,
    handleRemoveSection,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,

    // Utility
    reset,
  };
};
